/**
 * Pazaryeri fiyatlaması — the two tables of migration 20261104000001 and the view
 * that resolves a listing's overrides against its channel.
 *
 * Nothing here computes a price: that is `computeChannelPrice` in
 * packages/core/src/pricing/channel.ts, and the migration's header explains why
 * there is exactly one implementation of it. This file reads parameters and writes
 * them back.
 *
 * The row types are written out by hand rather than taken from `Tables<…>`, the
 * same way api/market.ts does it: the screen has to compile and ship before anyone
 * runs `db push`, and the generated types will not know these relations until then.
 * Reconcile the two when the types are regenerated.
 */
import type { AslenClient } from '../db';
import type { CommissionBase, PriceRounding } from '../pricing';
import { unwrap } from './errors';

/** A `public.sales_channels` row. */
export interface SalesChannel {
  id: string;
  company_id: string;
  code: string;
  name: string;
  currency: string;
  colour: string | null;
  commission_pct: number;
  commission_base: CommissionBase;
  commission_vat_incl: boolean;
  /** Komisyon hakedişten düşülerek mi tahsil ediliyor, yoksa ayrı faturayla mı. */
  commission_in_payout: boolean;
  fee_vat_pct: number;
  service_fee: number;
  service_fee_vat_incl: boolean;
  shipping_fee: number;
  shipping_fee_vat_incl: boolean;
  /** true: taşıyıcının rakamı posta hizmet bedelini içeriyor; false: `eph_pct` kadar eklenir. */
  shipping_fee_eph_incl: boolean;
  /** Evrensel posta hizmeti katkı payı + BTK idari ücreti, % (6475 s.K. m.15 + Yön. m.18). */
  eph_pct: number;
  /** Taşıyıcının desi tarifesi; doluyken ve ilanın desisi ölçülmüşken kargo buradan okunur. */
  shipping_tariff: { carrier?: string; effective_from?: string; vatIncl: boolean; ephIncl?: boolean; rows: Record<string, number> } | null;
  ads_pct: number;
  ads_per_order: number;
  return_pct: number;
  return_ship_legs: number;
  return_scrap_pct: number;
  payout_days: number;
  withholding_pct: number;
  /** Beklenen kampanya indirimi, % — liste ile fiilen satılan fiyat arasındaki fark. */
  campaign_discount_pct: number;
  /** Kurumlar vergisi oranı, % — fiyatı etkilemez, net kâr köprüsü için. */
  corporate_tax_pct: number;
  rounding: PriceRounding;
  /** Per-field provenance: `{ service_fee: { assumed: true, note: '…' } }`. */
  sources: Record<string, { assumed: boolean; note: string }>;
  notes: string | null;
  is_active: boolean;
  sort: number;
}

/**
 * A `public.v_channel_listings` row: the listing, its channel with every override
 * already applied, and the three company facts the calculation starts from — the
 * unit cost, the cost of money and the product's KDV rate.
 */
export interface ChannelListingRow extends Omit<SalesChannel, 'id' | 'code' | 'name' | 'notes' | 'sort' | 'is_active'> {
  id: string;
  channel_id: string;
  item_id: string;
  pack_qty_base: number;
  title: string | null;
  channel_sku: string | null;
  desi: number | null;
  is_active: boolean;
  notes: string | null;
  packaging_cost: number;
  /** Bir ambalaj biriminin kaç baz birim aldığı; null = ölçülmedi. */
  packaging_unit_qty: number | null;
  labour_cost: number;
  /** Genel gider payı, ilan başına, KDV hariç — aylık havuz ÷ normal kapasite. */
  overhead_cost: number;
  /** Malın stokta beklediği gün; vade maliyeti buna + ödeme vadesine göre işler. */
  inventory_days: number | null;
  other_cost: number;
  target_margin_pct: number;
  list_price: number | null;
  list_price_set_at: string | null;
  channel_code: string;
  channel_name: string;
  sku: string;
  name_tr: string;
  base_unit: string;
  stock_unit: string;
  item_status: string;
  /** Null when no cost is known at all — the screen must say so, not price from zero. */
  unit_cost_base: number | null;
  unit_cost_source: 'average' | 'standard' | 'last_in' | 'last_purchase' | null;
  /** Null when the company has no capital_cost_rates row for the currency yet. */
  capital_annual_pct: number | null;
  vat_pct: number | null;
}

/** What `saveChannelListing` accepts; `id` present means update. */
export interface ChannelListingInput {
  id?: string;
  company_id: string;
  channel_id: string;
  item_id: string;
  pack_qty_base: number | string;
  title?: string | null;
  channel_sku?: string | null;
  desi?: number | string | null;
  packaging_cost?: number | string;
  packaging_unit_qty?: number | string | null;
  labour_cost?: number | string;
  overhead_cost?: number | string;
  inventory_days?: number | string | null;
  campaign_discount_pct?: number | string | null;
  other_cost?: number | string;
  commission_pct?: number | string | null;
  shipping_fee?: number | string | null;
  service_fee?: number | string | null;
  return_pct?: number | string | null;
  target_margin_pct?: number | string;
  list_price?: number | string | null;
  list_price_set_at?: string | null;
  notes?: string | null;
}

/** PostgREST codes for "this relation does not exist yet" (migration not pushed). */
const MISSING_RELATION = new Set(['PGRST205', '42P01']);

type Loose = {
  from: (t: string) => {
    select: (c: string) => Record<string, (...a: never[]) => unknown>;
    update: (v: unknown) => Record<string, (...a: never[]) => unknown>;
    insert: (v: unknown) => Record<string, (...a: never[]) => unknown>;
  };
};

interface Res<T> {
  data: T | null;
  error: { code?: string; message: string } | null;
}

/**
 * Every channel the company sells through, in display order.
 *
 * Returns `[]` when the relation is missing rather than throwing, so the pricing
 * tab can fall back to the built-in defaults (`CHANNEL_DEFAULTS`) and still answer
 * the question before `db push` has run. Any other error is a real one: an operator
 * who cannot see costs gets an empty list from RLS, which is also `[]`, and the tab
 * tells those two apart by the role it already knows.
 */
export async function listSalesChannels(client: AslenClient, companyId: string): Promise<SalesChannel[]> {
  const q = (client as unknown as Loose).from('sales_channels').select('*') as {
    eq: (c: string, v: string) => { is: (c: string, v: null) => { order: (c: string) => Promise<Res<SalesChannel[]>> } };
  };
  const res = await q.eq('company_id', companyId).is('deleted_at', null).order('sort');
  if (res.error && MISSING_RELATION.has(res.error.code ?? '')) return [];
  return unwrap(res as never) as SalesChannel[];
}

/** Every listing of one item, newest pack size last. */
export async function listChannelListings(client: AslenClient, companyId: string, itemId: string): Promise<ChannelListingRow[]> {
  const q = (client as unknown as Loose).from('v_channel_listings').select('*') as {
    eq: (c: string, v: string) => { eq: (c: string, v: string) => { order: (c: string) => Promise<Res<ChannelListingRow[]>> } };
  };
  const res = await q.eq('company_id', companyId).eq('item_id', itemId).order('pack_qty_base');
  if (res.error && MISSING_RELATION.has(res.error.code ?? '')) return [];
  return unwrap(res as never) as ChannelListingRow[];
}

/**
 * Insert or update one listing and return its id.
 *
 * `list_price_set_at` is stamped here and not by a trigger, because a listing is
 * saved on every parameter change and only *some* of those changes are a new
 * price; a trigger could not tell the difference without comparing the old row.
 */
export async function saveChannelListing(client: AslenClient, input: ChannelListingInput): Promise<string> {
  const { id, ...row } = input;
  const table = (client as unknown as Loose).from('channel_listings');
  if (id) {
    const q = table.update(row) as unknown as {
      eq: (c: string, v: string) => { select: (c: string) => { single: () => Promise<Res<{ id: string }>> } };
    };
    const res = await q.eq('id', id).select('id').single();
    return (unwrap(res as never) as { id: string }).id;
  }
  const q = table.insert(row) as unknown as { select: (c: string) => { single: () => Promise<Res<{ id: string }>> } };
  const res = await q.select('id').single();
  return (unwrap(res as never) as { id: string }).id;
}

/** Soft-delete: the row keeps its history the way every other table here does. */
export async function archiveChannelListing(client: AslenClient, id: string): Promise<void> {
  const q = (client as unknown as Loose).from('channel_listings').update({ deleted_at: new Date().toISOString() }) as unknown as {
    eq: (c: string, v: string) => Promise<Res<null>>;
  };
  unwrap((await q.eq('id', id)) as never);
}

/** Channel-wide terms: a new commission applies to every listing at once. */
export async function saveSalesChannel(client: AslenClient, id: string, patch: Partial<SalesChannel>): Promise<void> {
  const q = (client as unknown as Loose).from('sales_channels').update(patch) as unknown as {
    eq: (c: string, v: string) => Promise<Res<null>>;
  };
  unwrap((await q.eq('id', id)) as never);
}
